import {
    ChatContainerBaseConfig,
    ChatLoaderBaseConfig,
    ChatControllerBaseConfig,
    ChatRouteInfra,
    ChatRouters,
    ChatViewBaseConfig,
    ThreadContextValues,
    ChatContextProvider,
} from './base';
import { ChatRouteInit } from './chatRouteConfig';
import { ChatRoute } from './chatRouteInterface';
import {
    createCollectionContextContainer,
    createCollectionContextController,
    createSolutionContextContainer,
    createSolutionContextController,
    createThreadContextContainer,
    createThreadContextController,
    createThreadOptionContainer,
    createThreadOptionController,
    createComposerController,
    createComposerContainer,
} from './composer';
import { createMessageGroupContainer, createMessageGroupController } from './group';
import {
    createMessageContextItemContainer,
    createMessageItemContainer,
    createMessageItemController,
    createMessageSolutionContextItemContainer,
} from './message';
import { createMessageSolutionContextItemLoader } from './message';
import { createMessageSolutionContextItemController } from './message';
import {
    createCallToolContainer,
    createCallToolController,
    createCallToolLoader,
    createMessagePartContainer,
    createMessagePartController,
    createSolutionToolContainer,
    createSolutionToolController,
    createSolutionToolLoader,
} from './part';
import { createMessageSectionContainer, createMessageSectionController } from './section';
import {
    createSolutionContextItemContainer,
    createSolutionContextItemController,
    createSolutionContextItemLoader,
    createThreadContextItemContainer,
    createThreadDetailController,
} from './thread';
import React from 'react';
import { assert } from '@varos/util-typescript';
import {
    createCallRequestNewContainer,
    createCallRequestNewController,
    createCallRequestNewLoader,
    createCallRequestOverlayView,
    createCallRequestOverlayContainer,
    createCallRequestOverlayController,
    createCallRequestUpsertContainer,
    createCallRequestUpsertLoader,
    createCallRequestUpsertController,
    createCallRequestDetailContainer,
    createCallRequestDetailLoader,
    createCallRequestDetailController,
} from './call';

export function createChatRoute(config: ChatRouteInit): ChatRoute {
    const infra: ChatRouteInfra = {};

    const router: ChatRouters = {};

    const ThreadContext = React.createContext<ThreadContextValues | null>(null);

    const contexts: ChatContextProvider = {
        ...config.context,
        thread: {
            useContext() {
                const context = React.useContext(ThreadContext);
                assert(context, 'ThreadContext is not set');
                return context;
            },
            Provider(props) {
                return (
                    <ThreadContext.Provider value={props.value}>
                        {props.children}
                    </ThreadContext.Provider>
                );
            },
        },
    };

    const viewConfig: ChatViewBaseConfig = {
        UI: config.UI,
        kernel: config.kernel,
        Layout: config.Layout,
        router,
        infra,
    };

    const controllerConfig: ChatControllerBaseConfig = {
        kernel: config.kernel,
        infra,
        controller: config.controller,
    };

    const loaderConfig: ChatLoaderBaseConfig = {
        repository: config.repository,
    };

    const containerConfig: ChatContainerBaseConfig = {
        kernel: config.kernel,
        UI: config.UI,
        router,
        repository: config.repository,
        infra,
        external: config.external,
        context: contexts,
        state: config.state,
        hook: config.hook,
    };

    const Message = {
        Item: createMessageItemContainer(containerConfig, createMessageItemController()),
        Group: createMessageGroupContainer(
            containerConfig,
            createMessageGroupController()
        ),
        Section: createMessageSectionContainer(
            containerConfig,
            createMessageSectionController()
        ),
        Part: createMessagePartContainer(
            {
                ...containerConfig,
                Container: {
                    Tool: {
                        Call: createCallToolContainer(
                            containerConfig,
                            createCallToolLoader(containerConfig),
                            createCallToolController({
                                ...controllerConfig,
                                // timeoutMs: 15 * 60000, // 15 minutes
                                timeoutMs: 15000,
                                // NOTE set to true for easier debugging and development
                                autoAccept: false,
                            })
                        ),
                        Solution: createSolutionToolContainer(
                            containerConfig,
                            createSolutionToolLoader(loaderConfig),
                            createSolutionToolController(controllerConfig)
                        ),
                    },
                },
            },
            createMessagePartController()
        ),
    };

    const Thread = {
        Option: createThreadOptionContainer(
            containerConfig,
            createThreadOptionController(controllerConfig)
        ),
        Context: {
            Select: createThreadContextContainer(
                containerConfig,
                createThreadContextController(controllerConfig)
            ),
        },
    };

    // Create context containers for nested selection
    const solutionController = createSolutionContextController(controllerConfig);
    const SolutionContainer = createSolutionContextContainer(
        containerConfig,
        solutionController
    );

    const collectionController = createCollectionContextController(controllerConfig);
    const CollectionContainer = createCollectionContextContainer(
        containerConfig,
        collectionController
    );

    // Create composer infrastructure for chat functionality
    const composerController = createComposerController(controllerConfig);
    const ComposerContainer = createComposerContainer(
        containerConfig,
        composerController
    );

    const ContextItemContainer = createThreadContextItemContainer({
        Container: {
            Solution: createSolutionContextItemContainer(
                containerConfig,
                createSolutionContextItemLoader(loaderConfig),
                createSolutionContextItemController(controllerConfig)
            ),
        },
    });

    const MessageContextItemContainer = createMessageContextItemContainer({
        Container: {
            Solution: createMessageSolutionContextItemContainer(
                containerConfig,
                createMessageSolutionContextItemLoader(loaderConfig),
                createMessageSolutionContextItemController(controllerConfig)
            ),
        },
    });

    const Call = {
        Request: {
            Overlay: createCallRequestOverlayContainer(
                containerConfig,
                createCallRequestOverlayController(controllerConfig),
                createCallRequestOverlayView({
                    ...viewConfig,
                    Container: {
                        Call: {
                            Upsert: createCallRequestUpsertContainer(
                                {
                                    ...containerConfig,
                                    Container: {
                                        New: createCallRequestNewContainer(
                                            containerConfig,
                                            createCallRequestNewLoader(loaderConfig),
                                            createCallRequestNewController(
                                                controllerConfig
                                            )
                                        ),
                                        Detail: createCallRequestDetailContainer(
                                            containerConfig,
                                            createCallRequestDetailLoader(loaderConfig),
                                            createCallRequestDetailController(
                                                controllerConfig
                                            )
                                        ),
                                    },
                                },
                                createCallRequestUpsertLoader(loaderConfig),
                                createCallRequestUpsertController(controllerConfig)
                            ),
                        },
                    },
                })
            ),
        },
    };

    return {
        context: {
            chat: contexts,
        },
        controller: {
            thread: {
                detail: createThreadDetailController(controllerConfig),
            },
        },
        Container: {
            Call: {
                Request: {
                    Overlay: Call.Request.Overlay,
                },
            },
            Composer: ComposerContainer,
            Thread: {
                Option: Thread.Option,
                Context: {
                    Select: Thread.Context.Select,
                    Item: ContextItemContainer,
                    Collection: CollectionContainer,
                    Solution: SolutionContainer,
                },
            },
            Message: {
                Item: Message.Item,
                Group: Message.Group,
                Section: Message.Section,
                Part: Message.Part,
                Context: {
                    Item: MessageContextItemContainer,
                },
            },
        },
    };
}
